import { CdnFile } from '@services/core/cdn/file/file';
import { useGetNewsCategoriesMutation } from '@services/product/news/news-categories/news-categories.endpoints';
import { NewsCategory } from '@services/product/news/news-categories/news-categories.types';
import {
    useCreateNewsMutation,
    useGetNewsMutation,
    useUpdateNewsMutation,
} from '@services/product/news/news.endpoints';
import { News, NewsToCreate } from '@services/product/news/news.types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useAddNewsPageVM = () => {
    const navigate = useNavigate();
    const { id, duplicateId } = useParams();
    const [news, setNews] = useState<News>();
    const [newsDocuments, setNewsDocuments] = useState<CdnFile[]>([]);
    const [newsCategories, setNewsCategories] = useState<NewsCategory[]>([]);

    const deleteDocument = useCallback(
        (documentId: number) => {
            setNewsDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        },
        [newsDocuments]
    );

    const addDocument = useCallback(
        (file: CdnFile) => {
            setNewsDocuments((prev) => [...(prev ?? []), file]);
        },
        [newsDocuments]
    );

    const editDocument = useCallback(
        (file: CdnFile) => {
            setNewsDocuments((prev) => prev.map((doc) => doc.id === file.id ? file : doc));
        },
        [newsDocuments]
    );

    const [
        getNewsCategories,
        {
            isLoading: isGetNewsCategoriesLoading,
            isError: isGetNewsCategoriesError,
            error: getNewsCategoriesError,
            isSuccess: isGetNewsCategoriesSuccess,
            data: getNewsCategoriesResponse
        }
    ] = useGetNewsCategoriesMutation();

    const [
        getNews,
        { isLoading: isGetNewsLoading, isError: isGetNewsError, error: getNewsError, isSuccess: isGetNewsSuccess, data: getNewsResponse }
    ] = useGetNewsMutation();

    const [createNews, { isLoading: isCreateNewsLoading, isSuccess: isCreateNewsSuccess, data: createNewsResponse }] =
        useCreateNewsMutation();

    const [updateNews, { isLoading: isUpdateNewsLoading, isSuccess: isUpdateNewsSuccess, data: updateNewsResponse }] =
        useUpdateNewsMutation();

    useEffect(() => {
        getNewsCategories();
    }, []);

    useEffect(() => {
        if (duplicateId) {
            getNews({ id: Number(duplicateId) });
        } else if (id) {
            getNews({ id: Number(id) });
        }
    }, [duplicateId, id, getNews]);

    useEffect(() => {
        if ((isUpdateNewsSuccess && updateNewsResponse) || (isCreateNewsSuccess && createNewsResponse)) {
            navigate(`/products-management/news`);
        }
    }, [navigate, isUpdateNewsSuccess, updateNewsResponse, isCreateNewsSuccess, createNewsResponse]);

    useEffect(() => {
        if (isGetNewsCategoriesLoading) return;
        if (isGetNewsCategoriesError && getNewsCategoriesError) {
            console.error(getNewsCategoriesError);
            return;
        }
        if (isGetNewsCategoriesSuccess && getNewsCategoriesResponse) {
            setNewsCategories(getNewsCategoriesResponse.data ?? []);
            return;
        }
    }, [
        isGetNewsCategoriesError,
        isGetNewsCategoriesSuccess,
        getNewsCategoriesError,
        getNewsCategoriesResponse,
        isGetNewsCategoriesLoading
    ]);

    useEffect(() => {
        if (isGetNewsLoading) return;
        if (isGetNewsError && getNewsError) {
            console.error(getNewsError);
            return;
        }
        if (isGetNewsSuccess && getNewsResponse) {
            setNews(getNewsResponse.data);
            setNewsDocuments(getNewsResponse.data?.documents || []);
            return;
        }
    }, [isGetNewsError, isGetNewsSuccess, getNewsError, getNewsResponse, isGetNewsLoading]);

    const submitForm = async (values: NewsToCreate) => {
        const finalValues = {
            ...values,
            documents: newsDocuments
        };

        if (news && id) {
            await updateNews({ id: Number(id), news: { ...finalValues, id: Number(id) } });
        } else {
            await createNews(finalValues);
        }
    };

    return {
        news,
        isEdit: !!id,
        submitForm,
        isLoading: isCreateNewsLoading || isGetNewsLoading || isUpdateNewsLoading || isGetNewsCategoriesLoading,
        newsCategories,
        deleteDocument,
        addDocument,
        newsDocuments,
        editDocument
    };
};
